/**
 * BECI public-page analytics
 *
 * Mirrors the Foodleap tracking pattern:
 *  Virtual page views are handled centrally by the router afterEach hook.
 *  1. Scroll depth         — 25/50/75/100% milestones per page
 *  2. CTA clicks           — buttons and navigation links
 *  3. Content impressions  — hub/partner cards entering the viewport
 *
 * Usage (Options API):
 *   import { createScrollDepthTracker, trackBECICta, trackBECICard } from '@/util/useBECITracking.js'
 *
 *   mounted() {
 *     this._scrollTracker = createScrollDepthTracker('BECI Journeys')
 *     this._scrollTracker.start()
 *   },
 *   beforeUnmount() {
 *     this._scrollTracker?.stop()
 *   }
 */

import { trackMatomoEvent } from './analytics.js'
import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '../constants/analytics-constants.js'

const _paq = () => (window._paq = window._paq || [])

// ── Scroll depth ──────────────────────────────────────────────────────────────

export function createScrollDepthTracker (pageName) {
  const milestones = [25, 50, 75, 100]
  const fired = new Set()

  const handler = () => {
    const pct = Math.round(
      ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
    )
    milestones.forEach(m => {
      if (pct >= m && !fired.has(m)) {
        fired.add(m)
        trackMatomoEvent(
          MATOMO_EVENT_CATEGORIES.BECI_ENGAGEMENT,
          MATOMO_EVENT_ACTIONS.SCROLL_DEPTH,
          `${pageName} ${m}%`
        )
      }
    })
  }

  return {
    start () { window.addEventListener('scroll', handler, { passive: true }) },
    stop () { window.removeEventListener('scroll', handler) },
  }
}

// ── CTA clicks ────────────────────────────────────────────────────────────────
// Format: "Page › Section › Label"  or  "Page › Label"

export function trackBECICta (pageName, label, section = null) {
  const name = section
    ? `${pageName} › ${section} › ${label}`
    : `${pageName} › ${label}`
  trackMatomoEvent(
    MATOMO_EVENT_CATEGORIES.BECI_ENGAGEMENT,
    MATOMO_EVENT_ACTIONS.CTA_CLICK,
    name
  )
}

// ── Tab / pillar navigation ───────────────────────────────────────────────────

export function trackBECITabSwitch (pageName, tabLabel) {
  trackMatomoEvent(
    MATOMO_EVENT_CATEGORIES.BECI_ENGAGEMENT,
    MATOMO_EVENT_ACTIONS.CLICK,
    `${pageName} › Tab › ${tabLabel}`
  )
}

// ── Card clicks (hub / partner / member) ─────────────────────────────────────

export function trackBECICard (cardType, actorName) {
  trackMatomoEvent(
    MATOMO_EVENT_CATEGORIES.BECI_ENGAGEMENT,
    MATOMO_EVENT_ACTIONS.ACTOR_VISIT,
    `${cardType} › ${actorName}`
  )
}

// ── Content impressions ───────────────────────────────────────────────────────
// Elements must carry data-content-name and data-content-piece attributes.

export function createImpressionObserver (threshold = 0.5) {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const { contentName, contentPiece } = entry.target.dataset
          if (contentName && contentPiece) {
            _paq().push(['trackContentImpression', contentName, contentPiece, window.location.pathname])
          }
          observer.unobserve(entry.target)
        }
      })
    },
    { threshold }
  )
  return observer
}
